import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ApolloProvider } from "@apollo/client/react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import apolloClient from "./lib/apollo-client";
import { getAccessToken } from "./lib/token-utils";
import { ErrorTrackingProvider } from "./contexts/ErrorTrackingContext";
import { AuthErrorProvider } from "./contexts/AuthErrorContext";
import { AnalyticsTracker } from "@/components/AnalyticsTracker";
import DashboardLayout from "./components/DashboardLayout";
import Dashboard from "./pages/Dashboard";
import History from "./pages/History";
import SessionDetail from "./pages/SessionDetail";
import PersonalRooms from "./pages/PersonalRooms";
import Credits from "./pages/Credits";
import Calculator from "./pages/Calculator";
import Connectors from "./pages/Connectors";
import FlowBroadcaster from "./pages/FlowBroadcaster";
import Organization from "./pages/Organization";
import Settings from "./pages/Settings";
import NotFound from "./pages/NotFound";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Onboarding from "./pages/Onboarding";
import InviteAccept from "./pages/InviteAccept";
import InviteSetup from "./pages/InviteSetup";
import Impersonate from "./pages/Impersonate";

// Synchronous token check — prevents DashboardLayout (and its queries) from
// mounting at all when there's no access token. useEffect-based checks run
// after render, so queries would have already fired without this guard.
const ProtectedRoute = () => {
  if (!getAccessToken()) {
    return <Navigate to="/login" replace />;
  }
  return <DashboardLayout />;
};

const App = () => (
  <ApolloProvider client={apolloClient}>
    <AuthErrorProvider>
      <ErrorTrackingProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AnalyticsTracker />
            <Routes>
              {/* Public routes (no layout) */}
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/impersonate" element={<Impersonate />} />
              <Route path="/invite/:token" element={<InviteAccept />} />
              <Route path="/invite/:token/setup" element={<InviteSetup />} />
              
              {/* Protected routes (with layout) */}
              <Route element={<ProtectedRoute />}>
                <Route path="/" element={<Dashboard />} />
                <Route path="/rooms" element={<PersonalRooms />} />
                <Route path="/history" element={<History />} />
                <Route path="/history/:sessionId" element={<SessionDetail />} />
                <Route path="/credits" element={<Credits />} />
                <Route path="/calculator" element={<Calculator />} />
                <Route path="/connectors" element={<Connectors />} />
                <Route path="/connectors/flow-broadcaster" element={<FlowBroadcaster />} />
                <Route path="/organization" element={<Organization />} />
                <Route path="/settings" element={<Settings />} />
              </Route>
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </TooltipProvider>
      </ErrorTrackingProvider>
    </AuthErrorProvider>
  </ApolloProvider>
);

export default App;
